import React, { useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Anchor, KeyRound, CheckCircle2, ArrowLeft, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const canSubmit = useMemo(() => {
    return pw.length >= 8 && pw === pw2 && !!token;
  }, [pw, pw2, token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("Deze reset-link is ongeldig. Vraag een nieuwe aan.");
      return;
    }
    if (pw.length < 8) {
      setError("Wachtwoord moet minimaal 8 tekens zijn.");
      return;
    }
    if (pw !== pw2) {
      setError("De twee wachtwoorden zijn niet gelijk.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/password-reset/confirm`, {
        token,
        new_password: pw,
      });
      setDone(true);
      // Kleine vertraging zodat de gebruiker de bevestiging ziet
      setTimeout(() => navigate("/beheer/login"), 2200);
    } catch (err) {
      setError(err?.response?.data?.detail || "Wachtwoord bijwerken mislukt.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-reset-page"
      className="min-h-screen bg-[#F4F8FB] flex items-center justify-center p-4"
      style={{
        backgroundImage:
          "radial-gradient(at 20% 0%, rgba(107,192,232,0.25), transparent 50%), radial-gradient(at 80% 100%, rgba(46,156,214,0.18), transparent 45%)",
      }}
    >
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-1 mb-8 font-[Poppins] leading-none">
          <span className="text-3xl font-extrabold tracking-tight text-[#173A5E]">LEMMER</span>
          <span className="text-xl font-semibold text-[#2E9CD6] italic">Leeft</span>
        </Link>
        <div className="bg-white rounded-2xl shadow-xl border border-[#E4EEF5] p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-[#173A5E] flex items-center justify-center">
              <Anchor className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs font-[Poppins] font-semibold uppercase tracking-wider text-[#5A7594]">
                Beheer
              </p>
              <h1 className="text-2xl font-bold text-[#173A5E] leading-tight">
                Nieuw wachtwoord instellen
              </h1>
            </div>
          </div>

          {!token && (
            <div className="p-3 rounded-xl bg-[#FDECEE] border border-[#F5C3C8] text-[#8b1420] text-sm font-[Inter] flex items-start gap-2 mb-4">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Reset-link ontbreekt. Vraag opnieuw een link aan via <strong>Wachtwoord vergeten</strong>.</span>
            </div>
          )}

          {done ? (
            <div data-testid="admin-reset-success" className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-[#E6F5EA] flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-[#2A7A3F]" />
              </div>
              <p className="text-[#173A5E] font-[Poppins] font-semibold mb-2">
                Wachtwoord bijgewerkt
              </p>
              <p className="text-sm text-[#5A7594] font-[Inter] mb-4">
                Je wordt zo doorgestuurd naar de loginpagina.
              </p>
              <Loader2 className="w-4 h-4 animate-spin text-[#2E9CD6] mx-auto" />
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate className="space-y-4">
              <p className="text-sm text-[#5A7594] font-[Inter]">
                Kies een nieuw wachtwoord. Minimaal 8 tekens.
              </p>
              <div>
                <label htmlFor="pw" className="block text-sm font-[Poppins] font-semibold text-[#173A5E] mb-1.5">
                  Nieuw wachtwoord
                </label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8AA3B8]" />
                  <input
                    id="pw"
                    type="password"
                    data-testid="admin-reset-pw"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    autoComplete="new-password"
                    minLength={8}
                    placeholder="Minimaal 8 tekens"
                    className="w-full bg-white border border-[#E4EEF5] rounded-xl pl-9 pr-4 py-2.5 font-[Inter] text-[15px] text-[#173A5E] outline-none focus:border-[#2E9CD6] focus:ring-2 focus:ring-[#6BC0E8]/40 transition"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="pw2" className="block text-sm font-[Poppins] font-semibold text-[#173A5E] mb-1.5">
                  Herhaal wachtwoord
                </label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8AA3B8]" />
                  <input
                    id="pw2"
                    type="password"
                    data-testid="admin-reset-pw2"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    autoComplete="new-password"
                    className="w-full bg-white border border-[#E4EEF5] rounded-xl pl-9 pr-4 py-2.5 font-[Inter] text-[15px] text-[#173A5E] outline-none focus:border-[#2E9CD6] focus:ring-2 focus:ring-[#6BC0E8]/40 transition"
                  />
                </div>
              </div>

              {error && (
                <div data-testid="admin-reset-error" className="p-3 rounded-xl bg-[#FDECEE] border border-[#F5C3C8] text-[#8b1420] text-sm font-[Inter]">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || !canSubmit}
                data-testid="admin-reset-submit"
                className="ll-btn-primary w-full disabled:opacity-60 disabled:cursor-wait"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Wachtwoord opslaan
              </button>

              <div className="text-center pt-1">
                <Link
                  to="/beheer/login"
                  className="inline-flex items-center gap-1.5 text-sm font-[Poppins] font-semibold text-[#2E9CD6] hover:text-[#173A5E]"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Terug naar inloggen
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminResetPasswordPage;
